/* CONTEXT:START
Wave: 2
Status: implemented
Stub-Type: mock-data
Dependencies: [AdminDashboard, user-service, api-integration]
Risk-Level: medium
Validation-Method: e2e-test
Performance-Target: <2000ms
Last-Updated: 2025-08-05
CONTEXT:END */

import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Divider
} from '@mui/material';
import {
  Assignment,
  Person,
  History,
  TrendingUp,
  CheckCircle,
  Schedule,
  Refresh
} from '@mui/icons-material';
import { 
  PenholderAssignment, 
  PenholderMetrics,
  AssignmentsResponse, 
  MonthAssignment 
} from '../../types';
import { penholderApiService } from '../../services/PenholderApiService';
import { userService, User } from '../../services/userService';

/**
 * Admin component for managing penholder assignments
 * Allows assigning penholders for specific months and viewing assignment history
 */
export const PenholderAssignmentManager: React.FC = () => {
  // State management
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState<PenholderAssignment[]>([]);
  const [currentAssignment, setCurrentAssignment] = useState<PenholderAssignment | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Comprehensive assignments state
  const [comprehensiveAssignments, setComprehensiveAssignments] = useState<AssignmentsResponse | null>(null);
  const [comprehensiveLoading, setComprehensiveLoading] = useState(false);
  const [comprehensiveError, setComprehensiveError] = useState<string | null>(null);
  
  // User management state
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  
  // Assignment dialog state
  const [assignmentDialog, setAssignmentDialog] = useState<{
    open: boolean;
    month: number;
    year: number;
    selectedUserId: string;
  }>({
    open: false,
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    selectedUserId: ''
  });

  // Load assignment data and users on mount
  useEffect(() => {
    loadAssignmentData();
    loadUsers();
    loadComprehensiveAssignments();
  }, []);

  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      setUsersError(null);
      
      const fetchedUsers = await userService.listUsers();
      setUsers(fetchedUsers);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load users';
      setUsersError(errorMessage);
      console.error('Failed to load users:', err);
      
      // Keep empty array as fallback
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const loadAssignmentData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get authentication token
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (token) {
        // Set authentication token for API service
        penholderApiService.setAuthToken(token);
      }

      // Load current assignment
      const current = await penholderApiService.getCurrentAssignment();
      setCurrentAssignment(current);

      // Load all assignments for history
      const allAssignments = await penholderApiService.getAllAssignments();
      setAssignments(allAssignments.sort((a, b) => 
        new Date(b.assignedAt).getTime() - new Date(a.assignedAt).getTime()
      ));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assignment data');
    } finally {
      setLoading(false);
    }
  };

  const loadComprehensiveAssignments = async (year?: number) => {
    try {
      setComprehensiveLoading(true);
      setComprehensiveError(null);

      console.log('🔍 [DEBUG] Starting loadComprehensiveAssignments...');
      console.log('🔍 [DEBUG] Year parameter:', year);

      // Get authentication token
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      console.log('🔍 [DEBUG] Auth token available:', !!token);
      
      if (token) {
        // Set authentication token for API service
        penholderApiService.setAuthToken(token);
        
        console.log('🔍 [DEBUG] Calling penholderApiService.getComprehensiveAssignments...');
        
        // Load comprehensive assignments
        const comprehensiveData = await penholderApiService.getComprehensiveAssignments(year);
        
        console.log('🔍 [DEBUG] Raw API response:', JSON.stringify(comprehensiveData, null, 2));
        console.log('🔍 [DEBUG] Total assignments in response:', comprehensiveData.assignments?.length);
        console.log('🔍 [DEBUG] Total assigned count:', comprehensiveData.totalAssigned);
        console.log('🔍 [DEBUG] Total unassigned count:', comprehensiveData.totalUnassigned);
        
        // Log each assignment for debugging
        if (comprehensiveData.assignments) {
          comprehensiveData.assignments.forEach((assignment, index) => {
            console.log(`🔍 [DEBUG] Assignment ${index + 1}:`, {
              month: assignment.month,
              monthName: assignment.monthName,
              year: assignment.year,
              status: assignment.status,
              penholderName: assignment.penholder?.userName || 'None',
              penholderUserId: assignment.penholder?.userId || 'None'
            });
          });
        }
        
        // Check specifically for August and September
        const augustAssignment = comprehensiveData.assignments?.find(a => a.month === 8 && a.year === 2025);
        const septemberAssignment = comprehensiveData.assignments?.find(a => a.month === 9 && a.year === 2025);
        
        console.log('🔍 [DEBUG] August 2025 assignment:', augustAssignment);
        console.log('🔍 [DEBUG] September 2025 assignment:', septemberAssignment);
        
        setComprehensiveAssignments(comprehensiveData);
        
        console.log('🔍 [DEBUG] State updated successfully');
      } else {
        throw new Error('Authentication required');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load comprehensive assignments';
      console.error('❌ [DEBUG] Error in loadComprehensiveAssignments:', err);
      console.error('❌ [DEBUG] Error message:', errorMessage);
      setComprehensiveError(errorMessage);
    } finally {
      setComprehensiveLoading(false);
      console.log('🔍 [DEBUG] loadComprehensiveAssignments completed');
    }
  };

  const handleAssignPenholder = async () => {
    try {
      const selectedUser = users.find(u => u.id === assignmentDialog.selectedUserId);
      if (!selectedUser) {
        throw new Error('Selected user not found');
      }

      const newAssignment = await penholderApiService.assignPenholder(
        assignmentDialog.selectedUserId,
        selectedUser.name,
        assignmentDialog.month,
        assignmentDialog.year
      );

      // Refresh data
      await loadAssignmentData();
      await loadComprehensiveAssignments();
      
      // Close dialog
      setAssignmentDialog({
        open: false,
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        selectedUserId: ''
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign penholder');
    }
  };

  const getUserName = (userId: string, userName?: string): string => {
    // If userName is provided directly, use it
    if (userName) {
      return userName;
    }
    
    // Otherwise, try to look up by userId in the users array
    const user = users.find(u => u.id === userId);
    return user ? user.name : userId;
  };

  const getUserEmail = (userId: string): string => {
    const user = users.find(u => u.id === userId);
    return user ? user.email : '';
  };

  const getMonthName = (month: number): string => {
    return new Date(0, month - 1).toLocaleString('default', { month: 'long' });
  };

  const generateMonthOptions = () => {
    const options = [];
    const currentDate = new Date();
    
    // Generate options for current year and next year
    for (let year = currentDate.getFullYear(); year <= currentDate.getFullYear() + 1; year++) {
      const startMonth = year === currentDate.getFullYear() ? currentDate.getMonth() + 1 : 1;
      const endMonth = year === currentDate.getFullYear() + 1 ? 12 : 12;
      
      for (let month = startMonth; month <= endMonth; month++) {
        options.push({ month, year, label: `${getMonthName(month)} ${year}` });
      }
    }
    
    return options;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading assignment data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Typography variant="h4" gutterBottom>
        Penholder Assignment Manager
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Manage monthly penholder assignments and view assignment history
      </Typography>

      {/* Current Assignment Card */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="h6" gutterBottom>
                Current Assignment
              </Typography>
              {currentAssignment ? (
                <Box>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <Person color="primary" />
                    <Typography variant="body1">
                      <strong>{getUserName(currentAssignment.penholderUserId, currentAssignment.penholderUserName)}</strong>
                    </Typography>
                    <Chip label="Active" color="success" size="small" />
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {getUserEmail(currentAssignment.penholderUserId)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getMonthName(currentAssignment.month)} {currentAssignment.year}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Assigned: {new Date(currentAssignment.assignedAt).toLocaleDateString()}
                  </Typography>
                </Box>
              ) : (
                <Alert severity="warning">
                  No active penholder assignment for the current month
                </Alert>
              )}
            </Box>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadUsers}
                disabled={usersLoading}
                size="small"
              >
                {usersLoading ? 'Loading...' : 'Refresh Users'}
              </Button>
              <Button
                variant="contained"
                startIcon={<Assignment />}
                onClick={() => setAssignmentDialog({ ...assignmentDialog, open: true })}
                disabled={usersLoading || users.length === 0}
              >
                New Assignment
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* User Management Status */}
      {usersError && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Alert 
              severity="error" 
              action={
                <Button 
                  color="inherit" 
                  size="small" 
                  startIcon={<Refresh />}
                  onClick={loadUsers}
                  disabled={usersLoading}
                >
                  Retry
                </Button>
              }
            >
              <Typography variant="body2">
                <strong>User Loading Error:</strong> {usersError}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Unable to load users for penholder assignment. Please check your connection and try again.
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Assignments */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Assignments
          </Typography>
          
          {comprehensiveLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : comprehensiveError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {comprehensiveError}
            </Alert>
          ) : !comprehensiveAssignments ? (
            <Alert severity="info">
              No assignment data available
            </Alert>
          ) : (
            <Box>
              {/* Current Year Grid */}
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                {comprehensiveAssignments.currentYear} Assignments ({comprehensiveAssignments.totalAssigned} of 12 assigned)
              </Typography>
              
              <Box 
                display="grid" 
                gridTemplateColumns="repeat(auto-fit, minmax(280px, 1fr))" 
                gap={2} 
                sx={{ mb: 4 }}
              >
                {comprehensiveAssignments.assignments.map((assignment) => (
                  <Card 
                    key={`${assignment.year}-${assignment.month}`}
                    variant="outlined"
                    sx={{ 
                      backgroundColor: assignment.status === 'assigned' ? 'success.light' : 'grey.100',
                      borderColor: assignment.status === 'assigned' ? 'success.main' : 'grey.300'
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {assignment.monthName} {assignment.year}
                        </Typography>
                        <Chip
                          label={assignment.status === 'assigned' ? 'Assigned' : 'Unassigned'}
                          color={assignment.status === 'assigned' ? 'success' : 'default'}
                          size="small"
                          icon={assignment.status === 'assigned' ? <CheckCircle /> : <Schedule />}
                        />
                      </Box>
                      
                      {assignment.penholder ? (
                        <Box>
                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                            <Person fontSize="small" color="action" />
                            <Typography variant="body2">
                              {assignment.penholder.userName}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            Assigned: {new Date(assignment.penholder.assignedAt).toLocaleDateString()}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            By: {assignment.penholder.assignedByName}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No penholder assigned
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Box>

              {/* Future Assignments */}
              {comprehensiveAssignments.futureAssignments.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    Future Assignments ({comprehensiveAssignments.futureAssignments.length})
                  </Typography>
                  
                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Period</TableCell>
                          <TableCell>Penholder</TableCell>
                          <TableCell>Assigned Date</TableCell>
                          <TableCell>Assigned By</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {comprehensiveAssignments.futureAssignments.map((assignment) => (
                          <TableRow key={`${assignment.year}-${assignment.month}`}>
                            <TableCell>
                              <Typography variant="body2">
                                {assignment.monthName} {assignment.year}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Person fontSize="small" color="action" />
                                <Typography variant="body2">
                                  {assignment.penholder?.userName}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {assignment.penholder ? new Date(assignment.penholder.assignedAt).toLocaleDateString() : '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {assignment.penholder?.assignedByName || '-'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Assignment Dialog */}
      <Dialog
        open={assignmentDialog.open}
        onClose={() => setAssignmentDialog({ ...assignmentDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign Penholder</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Period</InputLabel>
                  <Select
                    value={`${assignmentDialog.month}-${assignmentDialog.year}`}
                    label="Period"
                    onChange={(e) => {
                      const [month, year] = e.target.value.split('-').map(Number);
                      setAssignmentDialog({ ...assignmentDialog, month, year });
                    }}
                  >
                    {generateMonthOptions().map((option) => (
                      <MenuItem key={`${option.month}-${option.year}`} value={`${option.month}-${option.year}`}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Penholder</InputLabel>
                  <Select
                    value={assignmentDialog.selectedUserId}
                    label="Penholder"
                    onChange={(e) => setAssignmentDialog({ ...assignmentDialog, selectedUserId: e.target.value })}
                    disabled={usersLoading}
                  >
                    {usersLoading ? (
                      <MenuItem disabled>
                        <Box display="flex" alignItems="center" gap={1}>
                          <CircularProgress size={16} />
                          <Typography variant="body2">Loading users...</Typography>
                        </Box>
                      </MenuItem>
                    ) : usersError ? (
                      <MenuItem disabled>
                        <Box>
                          <Typography variant="body2" color="error">
                            {usersError}
                          </Typography>
                          <Button 
                            size="small" 
                            startIcon={<Refresh />}
                            onClick={loadUsers}
                            sx={{ mt: 1 }}
                          >
                            Retry
                          </Button>
                        </Box>
                      </MenuItem>
                    ) : users.length === 0 ? (
                      <MenuItem disabled>
                        <Typography variant="body2" color="text.secondary">
                          No users available
                        </Typography>
                      </MenuItem>
                    ) : (
                      users.map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          <Box>
                            <Typography variant="body2">{user.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.email}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                The selected user will be notified of their assignment and will have access to the penholder dashboard 
                to review and edit submissions for {getMonthName(assignmentDialog.month)} {assignmentDialog.year}.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignmentDialog({ ...assignmentDialog, open: false })}>
            Cancel
          </Button>
          <Button
            onClick={handleAssignPenholder}
            variant="contained"
            disabled={!assignmentDialog.selectedUserId}
          >
            Assign Penholder
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
